// ═══════════════════════════════════════════════════════════════════════════════
// Shared modal: money received / money given line items (same data, title varies)
// ═══════════════════════════════════════════════════════════════════════════════

import type { SettlementMoneyReceivedDetail } from '@fdh/shared';
import { createPortal } from 'react-dom';
import {
  MODAL_BACKDROP_BOTTOM_CLASS,
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
  MODAL_PANEL_SURFACE_CLASS,
} from '../../lib/modalClasses';
import { ModalCircleCloseButton } from '../ui/ModalCircleCloseButton';
import {
  formatIsoDateForDailyBreakdownTable,
  formatSettlementPeriodDisplay,
} from '../../lib/settlementDisplayFormat';

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

export type MoneyReceivedDetailModalProps = {
  open: boolean;
  onClose: () => void;
  settlementStart: string;
  settlementEnd: string;
  rows: SettlementMoneyReceivedDetail[];
  t: TranslateFn;
  titleKey: string;
  titleId: string;
};

export const MoneyReceivedDetailModal = ({
  open,
  onClose,
  settlementStart,
  settlementEnd,
  rows,
  t,
  titleKey,
  titleId,
}: MoneyReceivedDetailModalProps) => {
  if (!open) {
    return null;
  }
  if (typeof document === 'undefined') {
    return null;
  }

  const formatReceivedDatetime = (value: string): string => {
    const matched = /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}:\d{2}))?$/.exec(value);
    if (!matched) {
      return value;
    }
    const timePart = matched[4] ?? '00:00';
    return `${matched[2]}-${matched[3]} ${timePart}`;
  };

  const formatModalAmount = (value: number): string => {
    const sign = value < 0 ? '-' : '';
    const trimmed = Number(Math.abs(value).toFixed(2)).toString();
    return `${sign}$${trimmed}`;
  };

  return createPortal(
    <div
      className={`${MODAL_BACKDROP_BOTTOM_CLASS} z-[120] ${MODAL_BACKDROP_SCRIM_CLASS}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`flex flex-col ${MODAL_PANEL_SURFACE_CLASS} ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t(titleKey)}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {t('settlement.period', { start: settlementStart, end: settlementEnd })}
            </p>
          </div>
          <ModalCircleCloseButton onClick={onClose} ariaLabel={t('common.closeViewer')} />
        </div>
        <div className="mt-4 overflow-y-auto pr-1">
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                    {t('settlement.moneyReceivedDetailReceivedDate')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                    {t('settlement.moneyReceivedDetailPeriod')}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-200">
                    {t('settlement.moneyReceivedDetailAmount')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((row, rowIndex) => {
                  const periodRangeSameCalendarYear =
                    row.periodStartDate.slice(0, 4) === row.periodEndDate.slice(0, 4);
                  const displayReceivedDate = row.receivedDate.includes(':')
                    ? formatReceivedDatetime(row.receivedDate)
                    : formatIsoDateForDailyBreakdownTable(row.receivedDate, periodRangeSameCalendarYear);
                  const [displayDatePart, displayTimePart] = displayReceivedDate.split(' ');
                  const displayPeriod = formatSettlementPeriodDisplay(
                    row.periodStartDate,
                    row.periodEndDate,
                    t
                  );
                  return (
                    <tr key={`${row.periodStartDate}-${row.periodEndDate}-${rowIndex}`}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                        <div>{displayDatePart}</div>
                        <div>{displayTimePart ?? '00:00'}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                        <div>{displayPeriod}</div>
                        {row.isAddOn ? (
                          <span className="mt-1 inline-flex rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            {t('settlement.moneyReceivedDetailAddOn')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                        {formatModalAmount(row.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
